// ArtworkUploader — Owns the entire post-upload flow:
//   1. Client-side CMYK preflight (rejects RGB with the Kolt notice).
//   2. Posts the file to /api/artwork/upload (raster -> PDF on the server).
//   3. Shows the technician animation while the upload + normalisation runs.
//   4. Renders the full-bleed preview via <ProofingView> immediately on success.
//   5. Auto-submits a human-designer proof request — no more "automated proof"
//      shortcut; every order is reviewed by our team before print.
//
// Lifts state up via three callbacks:
//   - onUploaded({ file_id, original_filename, source_filename })
//   - onCleared()    — when the customer re-uploads / clears their file
//   - onApproved()   — when the customer hits "Approve & Proceed"
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, AlertTriangle, UserCheck, Clock, ShieldCheck } from "lucide-react";

import { api } from "../lib/api";
import { preflightCmyk, CMYK_NOTICE } from "../lib/preflightCheck";
import TechnicianAnimation from "./TechnicianAnimation";
import ProofingView from "./ProofingView";

const MAX_BYTES = 50 * 1024 * 1024;

export default function ArtworkUploader({
  user,
  productSlug,
  productCustomDimensions,
  configHeightIn,
  configWidthIn,
  configShape,
  uploadedFile,
  onUploaded,
  onCleared,
  onApproved,
}) {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  const [preflightError, setPreflightError] = useState(null);
  const [resolutionInfo, setResolutionInfo] = useState(null); // {width, height, dpi, ok}
  const [designerNotified, setDesignerNotified] = useState(false);
  const [gateWarnings, setGateWarnings] = useState([]); // server-side AI / low-res flags

  const inspectResolution = (file, dataUrl) =>
    new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve({ ok: true, isImage: false });
        return;
      }
      const img = new Image();
      img.onload = () => {
        // Assume 72 dpi unless image has explicit DPI tags. Print quality
        // requires ≥150 dpi at final trim size. We use the largest dimension
        // as a proxy for print resolution sufficiency.
        const longestPx = Math.max(img.width, img.height);
        const estimatedDpiAt8In = Math.round(longestPx / 8);
        resolve({
          ok: estimatedDpiAt8In >= 150,
          isImage: true,
          width: img.width,
          height: img.height,
          estDpi: estimatedDpiAt8In,
        });
      };
      img.onerror = () => resolve({ ok: true, isImage: false });
      img.src = dataUrl;
    });

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("Max file size: 50MB");
      return;
    }
    if (!user) {
      toast.error("Sign in to upload artwork");
      navigate("/login");
      return;
    }

    setPreflightError(null);
    setResolutionInfo(null);
    setGateWarnings([]);
    setDesignerNotified(false);

    const pre = await preflightCmyk(f);
    if (!pre.ok) {
      setPreflightError(pre.reason);
      e.target.value = "";
      toast.error(pre.reason);
      return;
    }
    if (pre.warning) {
      setPreflightError(pre.warning);
      toast.warning("RGB file — we'll convert it to CMYK for press.");
    }

    // Resolution check (image files only — PDFs are vector-safe).
    let resInfo = { ok: true, isImage: false };
    if (f.type.startsWith("image/")) {
      const dataUrl = URL.createObjectURL(f);
      resInfo = await inspectResolution(f, dataUrl);
      URL.revokeObjectURL(dataUrl);
      setResolutionInfo(resInfo);
      if (!resInfo.ok) {
        toast.warning(
          `Low resolution: ~${resInfo.estDpi} dpi at 8" trim. Designer may flag this.`
        );
      }
    }

    setUploading(true);

    try {
      const fd = new FormData();
      fd.append("file", f);
      if (pre.needsConversion) fd.append("convert_to_pdf", "true");
      const { data } = await api.post("/artwork/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setGateWarnings(data.gatekeeper?.warnings || []);
      if ((data.gatekeeper?.warnings || []).some((w) => w.code === "rgb_colour")) setPreflightError(null);
      (data.gatekeeper?.warnings || []).forEach((w) =>
        w.severity === "error" ? toast.error(w.title) : toast.warning(w.title));
      onUploaded({
        file_id: data.file_id,
        original_filename: data.original_filename,
        source_filename: f.name,
      });

      // Build the high-res preview for ProofingView.
      if (f.type.startsWith("image/")) {
        setPreviewUrl(URL.createObjectURL(f));
        setPreviewIsPdf(false);
      } else {
        try {
          const { data: png } = await api.get(`/artwork/${data.file_id}/preview`, {
            responseType: "blob",
            // Guests have no session — the upload hands back a token that
            // authorises reads of their own file.
            params: data.access_token ? { token: data.access_token } : undefined,
          });
          setPreviewUrl(URL.createObjectURL(png));
          setPreviewIsPdf(false);
        } catch {
          setPreviewUrl(null);
          setPreviewIsPdf(true); // fallback: ProofingView will show the file card.
        }
      }

      // Brief animation pause so the technician animation feels intentional.
      await new Promise((r) => setTimeout(r, 1200));

      // Always route to a human designer — no more automated shortcut.
      try {
        await api.post("/admin/proof-requests", {
          file_id: data.file_id,
          notes: `Auto-routed designer proof for product ${productSlug || "unknown"}.`,
          product_slug: productSlug,
        });
        setDesignerNotified(true);
      } catch (err) {
        // The customer can still review their preview, but flag the queue
        // failure so the press team can pick it up manually.
        console.warn("Designer queue submit failed:", err);
        toast.error(
          err.response?.data?.detail ||
            "Could not auto-submit to designer — your file is uploaded; we'll catch this on our side."
        );
      }
      toast.success(`Artwork uploaded: ${data.original_filename}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const reuploadArtwork = () => {
    onCleared();
    setPreviewUrl((u) => {
      if (u) URL.revokeObjectURL(u);
      return null;
    });
    setPreflightError(null);
    setResolutionInfo(null);
    setGateWarnings([]);
    setDesignerNotified(false);
  };

  const acknowledgePreview = () => {
    // Customer has reviewed the preview. Designer is already in the queue.
    // Marking onApproved enables Add-to-Cart.
    onApproved?.();
    toast.success(
      "Preview acknowledged — Kolt designer will email a marked-up proof in ~2 hours."
    );
  };

  return (
    <div className="mt-5">
      <div className="overline mb-2">Print-ready artwork · Live preview</div>

      {preflightError && (
        <div
          className={`mb-3 p-3 rounded-sm border text-sm flex gap-2 ${preflightError === CMYK_NOTICE ? "border-amber-300 bg-amber-50 text-amber-900" : "border-red-300 bg-red-50 text-red-900"}`}
          data-testid="cmyk-warning"
        >
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${preflightError === CMYK_NOTICE ? "text-amber-600" : "text-red-600"}`} />
          <div className="leading-snug font-semibold">{preflightError}</div>
        </div>
      )}

      {gateWarnings.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="artwork-gatekeeper-warnings">
          {gateWarnings.map((w) => (
            <div
              key={w.code}
              className={`p-4 border-l-[3px] text-sm flex gap-3 ${
                w.severity === "error"
                  ? "border-red-500 bg-red-50 text-red-900"
                  : "border-amber-500 bg-amber-50 text-amber-900"
              }`}
              data-testid={`artwork-warning-${w.code}`}
            >
              <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${w.severity === "error" ? "text-red-600" : "text-amber-600"}`} />
              <div className="min-w-0">
                <div className="font-bold">{w.title}</div>
                <div className="leading-relaxed mt-1">{w.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {uploading ? (
        <div
          className="border-2 border-amber-300 bg-amber-50/40 rounded-sm p-6"
          data-testid="technician-uploading"
        >
          <TechnicianAnimation size={200} />
        </div>
      ) : uploadedFile ? (
        <>
          {/* Full-bleed LIVE PREVIEW rendered for every upload. Explicitly not
              labeled "Proof" — the real designer proof arrives via email +
              dashboard within 2 business hours (routed above via
              /admin/proof-requests). This surface exists so the customer
              can visually spot obvious issues immediately. */}
          <ProofingView
            previewUrl={previewUrl}
            previewIsPdf={previewIsPdf}
            filename={uploadedFile.original_filename}
            widthIn={Number(configWidthIn) || productCustomDimensions?.default_width || 3}
            heightIn={Number(configHeightIn) || productCustomDimensions?.default_height || 2}
            shape={configShape}
            onApprove={acknowledgePreview}
            onReupload={reuploadArtwork}
            testIdPrefix="upload-proof"
            title="Live Preview"
            subtitle="Instant on-screen mockup so you can spot obvious issues. This is not your final proof — a Kolt designer will email you a marked-up proof within 2 business hours."
            approveLabel="Looks Right — Continue"
            helperNote="Once you continue, we'll route this file to a human designer who will hand-check bleeds, colours, and trim, then send you a proof PDF to approve."
          />

          {/* Compact CMYK + resolution scan summary. */}
          <div
            className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-[0.18em]"
            data-testid="scan-summary"
          >
            <div className="border border-emerald-300 bg-emerald-50 text-emerald-800 py-2 px-3 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> CMYK Verified
            </div>
            <div
              className={`border py-2 px-3 flex items-center gap-2 ${
                resolutionInfo && !resolutionInfo.ok
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-emerald-300 bg-emerald-50 text-emerald-800"
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              {resolutionInfo?.isImage
                ? `${resolutionInfo.estDpi}dpi · ${resolutionInfo.width}×${resolutionInfo.height}`
                : "Vector / 300dpi OK"}
            </div>
            <div
              className={`border py-2 px-3 flex items-center gap-2 ${
                designerNotified
                  ? "border-kolt-orange bg-orange-50 text-orange-800"
                  : "border-slate-300 bg-slate-50 text-slate-600"
              }`}
              data-testid="designer-queue-status"
            >
              <Clock className="w-3.5 h-3.5" />
              {designerNotified ? "Designer Notified" : "Queueing…"}
            </div>
          </div>
        </>
      ) : (
        <label
          className="block border-2 border-dashed rounded-sm p-5 text-center transition-colors cursor-pointer border-slate-300 hover:border-[#333F48] hover:bg-slate-50"
          data-testid="artwork-dropzone"
        >
          <input
            type="file"
            className="hidden"
            onChange={handleFile}
            accept=".pdf,.tif,.tiff,.ai,.eps,.psd,.jpg,.jpeg,.png"
            disabled={uploading}
            data-testid="artwork-input"
          />
          <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
          <div className="font-bold text-sm text-slate-700">
            Drop PDF / TIFF / AI / EPS / PSD (CMYK)
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {user ? "Or click to browse — Max 50MB" : "Sign in required to upload"}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-2 font-bold">
            Live preview after upload · Every file hand-proofed by a Kolt designer
          </div>
        </label>
      )}

      {/* Human-designer fallback — always available. */}
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
        <UserCheck className="w-3.5 h-3.5 text-[#333F48] flex-shrink-0" />
        <div className="leading-snug">
          Don&apos;t have CMYK artwork yet, or want extra eyes on it?{" "}
          <a
            href="/quote?topic=design-assist"
            className="font-bold text-[#333F48] hover:text-[#DD6B33] underline-offset-2 hover:underline"
            data-testid="request-designer-help"
          >
            Have a Kolt designer help
          </a>{" "}
          — typically replies within 4 hours.
        </div>
      </div>
    </div>
  );
}
